import React, { useState } from 'react';
import { PendingUser } from '../../services/userManagement';

interface UserApprovalCardProps {
  user: PendingUser;
  onApprove: (userId: string) => Promise<void>;
  onReject: (userId: string, reason?: string) => Promise<void>;
}

/**
 * Component that displays a user card with approval/rejection actions
 */
const UserApprovalCard: React.FC<UserApprovalCardProps> = ({ 
  user, 
  onApprove, 
  onReject 
}) => {
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Format the creation date
  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleString();
  };

  // Handle approve click
  const handleApprove = async () => {
    if (isProcessing) return;
    
    setIsProcessing(true);
    setError(null);
    
    try {
      await onApprove(user.UserId);
    } catch (err) {
      setError('Failed to approve user');
      setIsProcessing(false);
    }
  };

  // Handle reject click
  const handleReject = async () => {
    if (isProcessing) return;
    
    setIsProcessing(true);
    setError(null);
    
    try {
      await onReject(user.UserId, rejectionReason);
    } catch (err) {
      setError('Failed to reject user');
      setIsProcessing(false);
    }
  };

  // Handle cancel rejection
  const handleCancelReject = () => {
    setShowRejectForm(false);
    setRejectionReason('');
  };

  return (
    <div className="user-approval-card">
      <div className="user-info">
        <h4 className="user-name">{user.FirstName} {user.LastName}</h4>
        <p className="user-email">{user.Email}</p>
        <p className="user-created">
          Registered: {formatDate(user.CreatedAt)}
        </p>
        <p className="user-id">
          <small>ID: {user.UserId}</small>
        </p>
      </div>

      {error && (
        <div className="approval-error">
          <p>{error}</p>
        </div>
      )}

      {!showRejectForm ? (
        <div className="approval-actions">
          <button 
            className="approve-button"
            onClick={handleApprove}
            disabled={isProcessing}
          >
            {isProcessing ? 'Processing...' : 'Approve'}
          </button>
          <button 
            className="reject-button"
            onClick={() => setShowRejectForm(true)}
            disabled={isProcessing}
          >
            Reject
          </button>
        </div>
      ) : (
        <div className="rejection-form">
          <label htmlFor={`reject-reason-${user.UserId}`}>
            Reason for rejection (optional):
          </label>
          <textarea
            id={`reject-reason-${user.UserId}`}
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            rows={3}
            placeholder="Provide a reason for rejection"
          />
          <div className="rejection-actions">
            <button 
              className="confirm-reject-button"
              onClick={handleReject}
              disabled={isProcessing}
            >
              {isProcessing ? 'Processing...' : 'Confirm Rejection'}
            </button>
            <button 
              className="cancel-button"
              onClick={handleCancelReject}
              disabled={isProcessing}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserApprovalCard;